import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { getVideos, getFighters, trackFeatureUsage } from '../services/api';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { 
  Video,
  Upload,
  Users,
  Crown,
  Lock
} from 'lucide-react';
import { Button } from '../components/ui/button';

export const VideosPage = () => {
  const { gym, isAdmin } = useAuth();
  const [videos, setVideos] = useState([]);
  const [fighters, setFighters] = useState([]);
  const [loading, setLoading] = useState(true);

  const isGoldTier = gym?.subscription_tier === 'gold' && gym?.subscription_status === 'active';

  useEffect(() => {
    if (isGoldTier) {
      loadData();
      trackFeatureUsage('videos', 'view').catch(() => {});
    } else {
      setLoading(false);
    }
  }, [isGoldTier]);

  const loadData = async () => {
    try {
      const [videosRes, fightersRes] = await Promise.all([
        getVideos(),
        getFighters()
      ]);
      setVideos(videosRes.data);
      setFighters(fightersRes.data);
    } catch (error) {
      toast.error('Failed to load videos');
    } finally {
      setLoading(false);
    }
  };

  const getFighterName = (fighterId) => {
    if (!fighterId) return 'General';
    const fighter = fighters.find(f => f.id === fighterId);
    return fighter?.name || 'Unknown Fighter';
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  // Show upgrade prompt for non-Gold subscribers
  if (!isGoldTier) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center py-20" data-testid="videos-upgrade-prompt">
          <div className="w-20 h-20 bg-yellow-500/20 rounded-full flex items-center justify-center mb-6">
            <Lock className="w-10 h-10 text-yellow-500" />
          </div>
          
          <h1 className="font-heading text-3xl font-bold text-white uppercase tracking-tight mb-4 text-center">
            Video Uploads
          </h1>
          
          <div className="flex items-center gap-2 mb-4">
            <Crown className="w-5 h-5 text-yellow-500" />
            <span className="text-yellow-500 uppercase text-sm font-bold tracking-wide">Gold Feature</span>
          </div>
          
          <p className="text-zinc-400 text-center max-w-md mb-8">
            Upload and organize training videos for your fighters. Review techniques, analyze sparring sessions, and track improvement over time.
          </p>
          
          <Link to="/subscription">
            <Button className="bg-red-600 hover:bg-red-700 text-white uppercase font-heading font-semibold" data-testid="upgrade-to-gold-btn">
              Upgrade to Gold
            </Button>
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="videos-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-white uppercase tracking-tight">
              Training Videos
            </h1>
            <p className="text-zinc-400 mt-1">{videos.length} videos uploaded</p>
          </div>
          {isAdmin && (
            <Button 
              className="bg-red-600 hover:bg-red-700 text-white uppercase font-heading font-semibold tracking-wide"
              data-testid="upload-video-btn"
              onClick={() => toast.info('S3 integration requires AWS credentials. Contact support for setup.')}
            >
              <Upload className="w-4 h-4 mr-2" />
              Upload Video
            </Button>
          )}
        </div>

        {/* Info Banner */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 bg-blue-600/20 border border-blue-600/50 rounded-sm"
        >
          <p className="text-blue-400 text-sm">
            <strong>Note:</strong> Video uploads require AWS S3 configuration. Contact support to set up cloud storage for your gym.
          </p>
        </motion.div>

        {/* Videos Grid */}
        {videos.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {videos.map((video, index) => (
              <motion.div
                key={video.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="p-6 bg-zinc-900/50 border border-zinc-800 rounded-sm hover:border-zinc-700 transition-colors"
                data-testid={`video-card-${video.id}`}
              >
                <div className="w-full h-32 bg-zinc-800 rounded-sm flex items-center justify-center mb-4">
                  <Video className="w-12 h-12 text-zinc-600" />
                </div>
                
                <h3 className="text-white font-medium truncate mb-2">{video.filename}</h3>
                
                {video.fighter_id && (
                  <Link 
                    to={`/fighters/${video.fighter_id}`}
                    className="text-zinc-400 hover:text-red-500 text-sm flex items-center gap-1 mb-2"
                  >
                    <Users className="w-4 h-4" />
                    {getFighterName(video.fighter_id)}
                  </Link>
                )}
                
                {video.description && (
                  <p className="text-zinc-500 text-sm truncate">{video.description}</p>
                )}
                
                <p className="text-zinc-600 text-xs mt-2">
                  {format(parseISO(video.created_at), 'MMM d, yyyy')}
                </p>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-20 text-zinc-500">
            <Video className="w-12 h-12 mb-4 opacity-50" />
            <p className="text-lg mb-2">No videos uploaded yet</p>
            <p className="text-sm text-zinc-600">Contact support to configure video uploads</p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default VideosPage;
